"use client";

import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { HorizontalLine } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { ResponsiveProp } from "@faire/web--source/slate/Theme/ResponsiveProp";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { trackCategoriesLayoutElementCtaClick } from "@faire/web-api--source/events/categories/click/layoutElementCta";
import { trackCategoriesLayoutElementItemClick } from "@faire/web-api--source/events/categories/click/layoutElementItem";
import { trackCategoriesLayoutElementView } from "@faire/web-api--source/events/categories/view/layoutElement";
import { IClickInfo } from "@faire/web-api--source/indigofair/data/IClickInfo";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import { SHOWROOM_APPAREL_C1_TITLE_BANNER } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/SHOWROOM_APPAREL_C1_TITLE_BANNER";
import React from "react";
import styled, { css } from "styled-components";

import {
  isSupportedBanner,
  LayoutElementBasedBanner,
} from "@faire/retailer/components/LayoutElementRenderer/LayoutElementBasedBanner";
import { LayoutElementBasedCarousel } from "@faire/retailer/components/LayoutElementRenderer/LayoutElementBasedCarousel";
import { TitleStyles } from "@faire/retailer/components/LayoutElementRenderer/styles/TitleStyles";
import { TrackedLinkContext } from "@faire/retailer/components/TrackedLink";
import { PAGE_TYPE } from "@faire/retailer/consts/PAGE_TYPE";
import {
  MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE,
  MAX_CONTENT_WIDTH,
  MAX_WIDE_SCREEN_CONTENT_WIDTH,
} from "@faire/retailer/features/Marketplace/styleConsts";
import { ShowroomEducationCarousel } from "@faire/retailer/features/Showroom/ApparelCategory/ShowroomEducationCarousel";
import { ShowroomTitleBanner } from "@faire/retailer/features/Showroom/ApparelCategory/ShowroomTitleBanner";
import { Category } from "@faire/retailer/models/Category/Category";
import { useRecordMilestoneByDay } from "@faire/retailer/stores/domain/useMilestone";
import { c1DividerStyles } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/LayoutElements/__internal__/styles.css";
import { ElevatedContentWrapper } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/LoggedInCategoryPageStyles";

import { ProductTileImpressionsProvider } from "./ProductTileImpressionsProvider";

interface IProps {
  layoutElement: ILayoutElement;
  className?: string;
  requestId?: string;
  category?: Category;
  isLoading?: boolean;
  index?: number;
  omitCategoryPageStyling?: boolean;
}

const DEFAULT_EMBLA_CAROUSEL_ARROW_OFFSET = 0;

export const RenderCategoryLayoutElement: React.FC<IProps> = (props) => {
  const { isMobile, isDesktopAndAbove } = useViewport();
  const recordMilestoneByDay = useRecordMilestoneByDay();

  const eventPathPrefix = `/category-homepage/${props.layoutElement.on_click_event_name}/${props.layoutElement.placement}`;

  const renderTallCategoryTileCarousel = () => {
    const MINIMUM_NUMBER_OF_CATEGORY_TILES = 3;

    const { layoutElement, requestId = "" } = props;

    const tiles =
      layoutElement.layout_element_data
        ?.map(
          (layoutElementData) =>
            layoutElementData.category_tile ??
            layoutElementData.simple_image_title
        )
        ?.filter(isNotUndefined) ?? [];

    if (tiles.length < MINIMUM_NUMBER_OF_CATEGORY_TILES) {
      return null;
    }

    const isBelowBreadcrumb =
      layoutElement.placement === ILayoutElement.Placement.BELOW_BREADCRUMB;

    return (
      <>
        <StyledLayoutElementBasedCarousel
          requestId={requestId ?? ""}
          layoutElement={{
            ...layoutElement,
            display_page:
              layoutElement.display_page ??
              ILayoutElement.DisplayPage.CATEGORY_PAGE,
          }}
          onCTAClick={trackCategoriesLayoutElementCtaClick}
          onItemClick={trackCategoriesLayoutElementItemClick}
          onVisible={trackCategoriesLayoutElementView}
          peekNext
          peekNextPercent={isMobile ? 10 : 4}
          itemGap={4}
          leftAlignTitle
          arrowOffsetX={DEFAULT_EMBLA_CAROUSEL_ARROW_OFFSET}
          $isBelowBreadcrumb={isBelowBreadcrumb}
          $maxWidth={MAX_WIDE_SCREEN_CONTENT_WIDTH}
          $lessTopMargin
        />
      </>
    );
  };

  const renderBrandCarousel = () => {
    const { category, layoutElement, requestId } = props;
    const navigationPath = category?.navigationPath;

    return (
      <StyledBrandCarousel $maxWidth={MAX_WIDE_SCREEN_CONTENT_WIDTH}>
        <StyledLayoutElementBasedCarouselBottomMargin
          requestId={requestId ?? ""}
          layoutElement={layoutElement}
          onCTAClick={trackCategoriesLayoutElementCtaClick}
          onItemClick={trackCategoriesLayoutElementItemClick}
          onVisible={trackCategoriesLayoutElementView}
          brandPageQueryParams={{
            refC: navigationPath,
          }}
          itemsPerSlide={{
            mobileAndAbove: 2,
            tabletAndAbove: 4,
            desktopAndAbove: 5,
            xLargeAndAbove: 6,
          }}
          arrowOffsetX={DEFAULT_EMBLA_CAROUSEL_ARROW_OFFSET}
        />
      </StyledBrandCarousel>
    );
  };

  const renderProductCarousel = () => {
    const { layoutElement, requestId, className, category } =
      props;

    const isBelowBreadcrumb =
      layoutElement.placement === ILayoutElement.Placement.BELOW_BREADCRUMB;

    const itemGap: ResponsiveProp<number> = {
      mobileAndAbove: 8,
      tabletAndAbove: 16,
    };

    return (
      <ProductTileImpressionsProvider
        requestId={requestId}
        category={category}
        onClickEventName={layoutElement.on_click_event_name}
      >
        <TrackedLinkContext.Provider
          value={{
            requestId: requestId,
            type: IClickInfo.Type.PRODUCT,
            containerName: eventPathPrefix,
          }}
        >
          <StyledLayoutElementBasedCarousel
            recordAdImpression
            requestId={requestId ?? ""}
            itemGap={itemGap}
            itemsPerSlide={{
              mobileAndAbove: 2,
              tabletAndAbove: 4,
              desktopAndAbove: 5,
              xLargeAndAbove: 6,
            }}
            $isBelowBreadcrumb={isBelowBreadcrumb}
            layoutElement={layoutElement}
            onCTAClick={trackCategoriesLayoutElementCtaClick}
            onItemClick={trackCategoriesLayoutElementItemClick}
            onVisible={trackCategoriesLayoutElementView}
            leftAlignTitle
            pageType={PAGE_TYPE.CATEGORY}
            showArrowCTA={isMobile}
            className={className}
            peekNextPercent={{
              mobileAndAbove: 10,
              tabletAndAbove: 0,
            }}
            productPageQueryParams={{
              refC: category?.navigationPath,
            }}
            arrowOffsetX={DEFAULT_EMBLA_CAROUSEL_ARROW_OFFSET}
            $maxWidth={MAX_WIDE_SCREEN_CONTENT_WIDTH}
          />
        </TrackedLinkContext.Provider>
      </ProductTileImpressionsProvider>
    );
  };

  const renderShowroomEducationCarousel = () => {
    const { layoutElement, requestId, category, className } = props;
    return (
      <StyledShowroomEducationCarousel
        layoutElement={layoutElement}
        requestId={requestId}
        category={category}
        className={className}
      />
    );
  };

  const renderShowroomTitleBanner = () => {
    const { layoutElement, requestId, category, className } = props;
    return (
      <MobileWrapper>
        <ShowroomTitleBanner
          layoutElement={layoutElement}
          requestId={requestId}
          category={category}
          className={className}
        />
      </MobileWrapper>
    );
  };

  const renderSampleRequestCarousel = () => {
    const { layoutElement, requestId } = props;
    const commonProps = {
      requestId: requestId ?? "",
      layoutElement,
      onItemClick: trackCategoriesLayoutElementItemClick,
      onVisible: trackCategoriesLayoutElementView,
      itemsPerSlide: {
        mobileAndAbove: 2,
        tabletAndAbove: 4,
        desktopAndAbove: 5,
        xLargeAndAbove: 6,
      },
      leftAlignTitle: true,
      arrowOffsetX: DEFAULT_EMBLA_CAROUSEL_ARROW_OFFSET,
    };

    const isBelowBreadcrumb =
      layoutElement.placement === ILayoutElement.Placement.BELOW_BREADCRUMB;

    return (
      <StyledSampleRequestCarousel
        $maxWidth={MAX_WIDE_SCREEN_CONTENT_WIDTH}
        $isBelowBreadcrumb={isBelowBreadcrumb}
      >
        <ResponsiveContentV2
          tabletAndBelow={
            <StyledLayoutElementBasedCarousel {...commonProps} itemGap={8} />
          }
          desktop={
            <StyledLayoutElementBasedCarousel {...commonProps} itemGap={16} />
          }
          xLargeAndAbove={<StyledLayoutElementBasedCarousel {...commonProps} />}
        />
      </StyledSampleRequestCarousel>
    );
  };

  const renderNoOpElement = () => {
    const { layoutElement, requestId } = props;
    return (
      <NoOpLayoutElementCarousel
        hideTitle
        layoutElement={layoutElement}
        requestId={requestId ?? ""}
        onItemClick={trackCategoriesLayoutElementItemClick}
        onVisible={trackCategoriesLayoutElementView}
        pageType={PAGE_TYPE.CATEGORY}
      />
    );
  };

  const renderDynamicAutoColumnGrid = () => {
    const { omitCategoryPageStyling, layoutElement, requestId } = props;

    return (
      <StyledLayoutElementBasedCarousel
        requestId={requestId ?? ""}
        layoutElement={layoutElement}
        $isBelowBreadcrumb={!omitCategoryPageStyling}
        $applyMobileMargins={!omitCategoryPageStyling}
      />
    );
  };

  const renderDynamicSparseGrid = () => {
    const { omitCategoryPageStyling, layoutElement, requestId } = props;
    return (
      <StyledLayoutElementBasedCarousel
        requestId={requestId ?? ""}
        layoutElement={layoutElement}
        hideTitle
        $isBelowBreadcrumb={!omitCategoryPageStyling}
        $applyMobileMargins={!omitCategoryPageStyling}
      />
    );
  };

  const onLayoutElementBannerVisible = (
    containerName: string,
    requestId: string,
    tokens: string
  ) => {
    const { layoutElement } = props;
    if (
      layoutElement.type ===
      ILayoutElement.Type.MID_PRICE_POINT_PLUS_CATEGORY_HOME_PAGE_HEADER_BANNER
    ) {
      recordMilestoneByDay(SHOWROOM_APPAREL_C1_TITLE_BANNER);
    }

    if (layoutElement.type === ILayoutElement.Type.MULTI_IMAGE_TITLE_BANNER) {
      const layoutElementDataTokens = layoutElement.layout_element_data
        ?.map(
          (layoutElementData) =>
            layoutElementData.simple_image_title?.event_tracking_token
        )
        .filter(isNotUndefined);
      const displayedBrandTokens = isDesktopAndAbove
        ? layoutElementDataTokens
        : layoutElementDataTokens?.slice(0, 1);
      trackCategoriesLayoutElementView(
        containerName,
        requestId,
        displayedBrandTokens?.join(" ") ?? ""
      );
      return;
    }

    trackCategoriesLayoutElementView(containerName, requestId, tokens);
  };

  const renderLayoutElementBasedBanner = () => {
    const { layoutElement, requestId } = props;
    return (
      <StyledLayoutElementBasedBanner
        layoutElement={layoutElement}
        requestId={requestId ?? ""}
        onCTAClick={trackCategoriesLayoutElementCtaClick}
        onItemClick={trackCategoriesLayoutElementItemClick}
        onVisible={onLayoutElementBannerVisible}
        pageType={PAGE_TYPE.CATEGORY}
      />
    );
  };

  const renderHorizontalLine = () => {
    return (
      <HorizontalLine
        data-test-id="layout-element-horizontal-line"
        className={c1DividerStyles}
      />
    );
  };

  if (props.isLoading) {
    return null;
  }

  switch (props.layoutElement.type) {
    case ILayoutElement.Type.TALL_CATEGORY_TILE:
      return renderTallCategoryTileCarousel();
    case ILayoutElement.Type.SMALL_MAKER_TILE_CAROUSEL:
      return renderBrandCarousel();
    case ILayoutElement.Type.PRODUCT_TILE_CAROUSEL:
    case ILayoutElement.Type.BUY_FROM_SAMPLES_CAROUSEL:
      return renderProductCarousel();
    case ILayoutElement.Type.SHOP_MID_PRICE_POINT_PLUS_PRODUCTS_BANNER:
      return renderShowroomEducationCarousel();
    case ILayoutElement.Type.MID_PRICE_POINT_PLUS_PAGE_TITLE:
      return renderShowroomTitleBanner();
    case ILayoutElement.Type.SAMPLE_REQUEST_CAROUSEL:
      return renderSampleRequestCarousel();
    case ILayoutElement.Type.NO_OP_LAYOUT_ELEMENT:
      return renderNoOpElement();
    case ILayoutElement.Type.DYNAMIC_SPARSE_GRID:
      return renderDynamicSparseGrid();
    case ILayoutElement.Type.DYNAMIC_GRID_OR_CAROUSEL:
      return renderDynamicAutoColumnGrid();
    case ILayoutElement.Type.HORIZONTAL_LINE:
      return renderHorizontalLine();
  }
  if (isSupportedBanner(props.layoutElement.type)) {
    return renderLayoutElementBasedBanner();
  }
  return null;
};

const fullWidthLayoutElement = css`
  width: calc(100% + ${MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE * 2}px);
  margin-left: -${MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE}px;
  margin-right: -${MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE}px;
`;
const wrapperStyle = css`
  ${responsiveMediaQueries.desktopAndAbove} {
    margin-bottom: 48px;
  }
  margin-bottom: 40px;

  ${responsiveMediaQueries.mobile} {
    ${fullWidthLayoutElement}
  }
`;

const StyledLayoutElementBasedBanner = styled(LayoutElementBasedBanner)<{
  layoutElement: ILayoutElement;
}>`
  ${(props) => {
    if (
      props.layoutElement.type === ILayoutElement.Type.FULL_WIDTH_IMAGE_BANNER
    ) {
      return css`
        ${responsiveMediaQueries.mobile} {
          margin-bottom: ${getSpacing("1/2x")};
        }
        ${responsiveMediaQueries.tabletAndAbove} {
          margin-bottom: ${getSpacing(12)};
        }
      `;
    }

    if (
      props.layoutElement.type === ILayoutElement.Type.COMMUNITY_CATEGORY_BANNER
    ) {
      return css`
        margin-bottom: ${getSpacing("3x")};
      `;
    }

    if (
      props.layoutElement.type === ILayoutElement.Type.MULTI_IMAGE_TITLE_BANNER
    ) {
      return css`
        width: 100%;
        max-width: 1920px;
        margin: 0 auto;

        ${responsiveMediaQueries.mobile} {
          background-color: unset;
        }
      `;
    }

    if (
      props.layoutElement.type ===
      ILayoutElement.Type.SHOWROOM_VALUES_TITLE_BANNER
    ) {
      return css`
        margin-bottom: ${getSpacing("2x")};
        ${responsiveMediaQueries.tabletAndBelow} {
          ${fullWidthLayoutElement}
        }
      `;
    }

    return css``;
  }}
`;

const NoOpLayoutElementCarousel = styled(LayoutElementBasedCarousel)`
  margin: 0;
`;

const StyledLayoutElementBasedCarousel = styled(LayoutElementBasedCarousel)<{
  $isBelowBreadcrumb?: boolean;
  $applyMobileMargins?: boolean;
  $maxWidth?: number;
  $lessTopMargin?: boolean;
}>`
  ${wrapperStyle}

  ${TitleStyles.Container} {
    ${responsiveMediaQueries.mobile} {
      padding-left: 20px;
    }
  }

  ${({ $isBelowBreadcrumb, $applyMobileMargins, $maxWidth, $lessTopMargin }) =>
    $isBelowBreadcrumb
      ? css`
          ${responsiveMediaQueries.tabletAndAbove} {
            width: 100%;
            ${$lessTopMargin
              ? css`
                  margin: ${getSpacing("2x")} auto ${getSpacing("5x")};
                `
              : css`
                  margin: ${getSpacing("4x")} auto 0;
                `}
            padding: ${getSpacing(0, "6x")};
          }
          ${responsiveMediaQueries.desktopAndAbove} {
            max-width: ${$maxWidth ?? MAX_CONTENT_WIDTH}px;
            ${$lessTopMargin
              ? css`
                  margin: ${getSpacing("2x")} auto ${getSpacing("6x")};
                `
              : css`
                  margin: ${getSpacing("5x")} auto 0;
                `}
          }
          ${responsiveMediaQueries.xLargeAndAbove} {
            padding: ${getSpacing(0, "6x")};
          }
          ${responsiveMediaQueries.mobile} {
            ${$lessTopMargin
              ? css`
                  margin: ${getSpacing("2x")} auto ${getSpacing("4x")};
                `
              : css`
                  margin: ${getSpacing("4x")} auto 0;
                `}
            width: 100%;
            ${TitleStyles.Container} {
              padding-left: ${getSpacing("2x")};
            }
            padding: 0px ${$applyMobileMargins ? getSpacing("2x") : "auto"};
          }
        `
      : ""}
`;

const StyledLayoutElementBasedCarouselBottomMargin = styled(
  StyledLayoutElementBasedCarousel
)`
  margin-bottom: ${getSpacing("4x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    margin-bottom: ${getSpacing("6x")};
  }
`;

const StyledSampleRequestCarousel = styled.div<{
  $maxWidth?: number;
  $isBelowBreadcrumb?: boolean;
}>`
  ${StyledLayoutElementBasedCarousel} {
    padding: ${getSpacing("2x", 0)};

    ${responsiveMediaQueries.tabletAndAbove} {
      ${({ $isBelowBreadcrumb }) =>
        $isBelowBreadcrumb
          ? css`
              padding: ${getSpacing("6x")};
            `
          : css`
              padding: ${getSpacing("5x")};
            `}
    }

    ${({ $isBelowBreadcrumb, $maxWidth }) =>
      $isBelowBreadcrumb && $maxWidth
        ? css`
            max-width: ${$maxWidth}px;
            margin: auto;
            width: auto;
          `
        : ""}
  }

  ${TitleStyles.AuxiliaryTitleArrow} {
    display: none;
    ${responsiveMediaQueries.mobile} {
      display: inline-block;
    }
  }

  ${TitleStyles.AuxiliaryTitleText} {
    display: none;
    ${responsiveMediaQueries.tabletAndAbove} {
      display: inline-block;
    }
  }
`;

const MobileWrapper = styled.div`
  ${responsiveMediaQueries.mobile} {
    width: calc(100% + ${MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE * 2}px);
    margin: 0 -${MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE}px;
  }
`;

const StyledShowroomEducationCarousel = styled(ShowroomEducationCarousel)`
  ${wrapperStyle}
`;

const StyledBrandCarousel = styled(ElevatedContentWrapper)`
  &&& {
    ${responsiveMediaQueries.mobile} {
      padding: 0 ${getSpacing("2x")};
      width: 100%;
    }
    ${responsiveMediaQueries.tabletAndAbove} {
      margin: 0 auto;
      padding: ${getSpacing(0, "6x")};
    }

    ${responsiveMediaQueries.desktopAndAbove} {
      margin: 0 auto;
    }
  }
`;
